/**
 * Header navigation of Hadiyati.
 *
 * Desktop: compact dropdown panels (snippets/hadiyati-nav-item.liquid). A top level entry
 * `[data-hdy-nav]` opens a panel; a row `[data-hdy-sub]` opens a second adjacent panel. Each node
 * has a separate toggle button `[data-hdy-toggle]` (the link navigates, the button only opens) and
 * a panel `[data-hdy-panel]`. Opens on hover (mouse only, with small delays so crossing the
 * junction between panels does not close anything), on click / Enter / Space of the button, closes
 * on Escape (focus back to the button), outside click, focus leaving, or a viewport change. One
 * open sub menu at a time per level.
 *
 * Mobile drawer: arrow buttons `[data-hdy-drawer-toggle]` unfold the list they control, inside the
 * drawer (snippets/hadiyati-drawer-menu.liquid). No hover behaviour.
 */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 240;
const NODE = '[data-hdy-nav], [data-hdy-sub]';

/** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
const timers = new WeakMap();

/** @param {Element} node */
const panelOf = (node) => /** @type {HTMLElement | null} */ (node.querySelector(':scope > [data-hdy-panel]'));

/** @param {Element} node */
const toggleOf = (node) =>
  /** @type {HTMLElement | null} */ (node.querySelector(':scope > [data-hdy-toggle], :scope > .menu-list__trigger > [data-hdy-toggle]'));

/** @param {Element} node */
const isOpen = (node) => node.hasAttribute('data-open');

/** @param {Element} node */
function clearTimer(node) {
  const t = timers.get(node);
  if (t) clearTimeout(t);
  timers.delete(node);
}

/**
 * Keeps a panel inside the viewport: the top panel aligns to the end when it would overflow, the
 * second panel opens on the other side.
 * @param {Element} node
 */
function placePanel(node) {
  const panel = panelOf(node);
  if (!panel) return;
  const flyout = panel.classList.contains('hdy-nav-panel--flyout');
  panel.removeAttribute('data-align');
  panel.removeAttribute('data-flip');
  const rect = panel.getBoundingClientRect();
  if (rect.right > window.innerWidth - 8) panel.setAttribute(flyout ? 'data-flip' : 'data-align', '');
}

/**
 * @param {Element} node
 * @param {boolean} open
 */
function setOpen(node, open) {
  clearTimer(node);
  if (isOpen(node) === open) return;
  const panel = panelOf(node);
  const toggle = toggleOf(node);
  if (open) {
    // One open node per level: close the siblings (and everything below them).
    const level = node.parentElement?.closest(NODE) || document;
    for (const other of level.querySelectorAll(NODE)) {
      if (other !== node && !other.contains(node) && !node.contains(other) && sameLevel(other, node)) setOpen(other, false);
    }
    node.setAttribute('data-open', '');
    if (panel) panel.inert = false;
    toggle?.setAttribute('aria-expanded', 'true');
    placePanel(node);
  } else {
    for (const child of node.querySelectorAll(NODE)) setOpen(child, false);
    node.removeAttribute('data-open');
    if (panel) panel.inert = true;
    toggle?.setAttribute('aria-expanded', 'false');
  }
}

/**
 * @param {Element} a
 * @param {Element} b
 */
function sameLevel(a, b) {
  const depth = (/** @type {Element} */ el) => {
    let n = 0;
    for (let p = el.parentElement?.closest(NODE); p; p = p.parentElement?.closest(NODE)) n++;
    return n;
  };
  return depth(a) === depth(b);
}

function closeAll() {
  for (const node of document.querySelectorAll(`${NODE}[data-open]`)) setOpen(node, false);
}

/**
 * @param {Element} node
 * @param {number} delay
 * @param {boolean} open
 */
function schedule(node, delay, open) {
  clearTimer(node);
  timers.set(
    node,
    setTimeout(() => setOpen(node, open), delay)
  );
}

const hoverCapable = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || !hoverCapable()) return;
  const node = event.target instanceof Element ? event.target.closest(NODE) : null;
  if (!node) return;
  // The node and its ancestors stay open while the pointer is anywhere inside them.
  for (let n = /** @type {Element | null} */ (node); n; n = n.parentElement?.closest(NODE) ?? null) {
    clearTimer(n);
    if (!isOpen(n)) schedule(n, OPEN_DELAY, true);
  }
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse' || !hoverCapable()) return;
  const node = event.target instanceof Element ? event.target.closest(NODE) : null;
  if (!node) return;
  const next = /** @type {Element | null} */ (event.relatedTarget instanceof Element ? event.relatedTarget : null);
  for (let n = /** @type {Element | null} */ (node); n; n = n.parentElement?.closest(NODE) ?? null) {
    if (next && n.contains(next)) break;
    schedule(n, CLOSE_DELAY, false);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const toggle = target?.closest('[data-hdy-toggle]');
  if (toggle) {
    const node = toggle.closest(NODE);
    if (node) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(node, !isOpen(node));
    }
    return;
  }
  const drawerToggle = target?.closest('[data-hdy-drawer-toggle]');
  if (drawerToggle) {
    event.preventDefault();
    const list = document.getElementById(drawerToggle.getAttribute('aria-controls') || '');
    if (!list) return;
    const open = drawerToggle.getAttribute('aria-expanded') !== 'true';
    drawerToggle.setAttribute('aria-expanded', String(open));
    list.hidden = !open;
    return;
  }
  // Click outside every open panel.
  if (!target?.closest(NODE)) closeAll();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = [...document.querySelectorAll(`${NODE}[data-open]`)];
  if (open.length === 0) return;
  // Deepest open node first: Escape closes one level at a time.
  const deepest = open.reduce((a, b) => (a.contains(b) ? b : a));
  const toggle = toggleOf(deepest);
  setOpen(deepest, false);
  if (toggle && deepest.contains(document.activeElement)) toggle.focus();
});

document.addEventListener('focusout', (event) => {
  const node = event.target instanceof Element ? event.target.closest(NODE) : null;
  if (!node) return;
  setTimeout(() => {
    for (let n = /** @type {Element | null} */ (node); n; n = n.parentElement?.closest(NODE) ?? null) {
      if (isOpen(n) && !n.contains(document.activeElement) && !n.matches(':hover')) setOpen(n, false);
    }
  }, 0);
});

/** Back to a clean state whenever the layout changes (desktop ↔ mobile, rotation). */
function reset() {
  closeAll();
  for (const toggle of document.querySelectorAll('[data-hdy-drawer-toggle][aria-expanded="true"]')) {
    toggle.setAttribute('aria-expanded', 'false');
    const list = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (list) list.hidden = true;
  }
}

window.matchMedia('(min-width: 750px)').addEventListener('change', reset);
window.addEventListener('resize', closeAll);
window.addEventListener('pageshow', reset);
